import { useEffect, useState } from "react";
import api from "../../lib/api";
import { UsersThree, ArrowUp, ArrowDown } from "@phosphor-icons/react";

/**
 * WeeklyVisitorsCard — sits next to UniqueVisitorsCard.
 *
 * Big number   = true weekly-unique visitors (distinct session_keys this
 *                Sun→Sat window). Jon visiting Mon+Thu counts as 1.
 * Sub-line     = raw page_view total this week (one human, multiple
 *                visits = multiple counts).
 * Delta badge  = % change vs prior 3-week average for the unique count.
 * Sparkline    = last 4 weeks of weekly-unique counts.
 *
 * Data: GET /api/admin/weekly-uniques?weeks=4 →
 *   { weeks, series: [{week_start, week_end, unique_visitors, total_visits}],
 *     this_week, last_week }
 */
export default function WeeklyVisitorsCard() {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/weekly-uniques?weeks=4")
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setErrored(true));
    return () => { mounted = false; };
  }, []);

  const weeks = data?.series || [];
  const thisWeek = data?.this_week || { unique_visitors: 0, total_visits: 0 };
  const prior = weeks.slice(0, -1);
  const priorAvg = prior.length
    ? prior.reduce((a, b) => a + (b.unique_visitors || 0), 0) / prior.length
    : 0;
  const delta = priorAvg > 0
    ? Math.round(((thisWeek.unique_visitors - priorAvg) / priorAvg) * 100)
    : null;
  const deltaTone = delta == null ? "muted" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const tone = thisWeek.unique_visitors > 0 ? "primary" : "muted";

  // Sparkline geometry (matches UniqueVisitorsCard so the row reads cohesively)
  const max = Math.max(1, ...weeks.map((w) => w.unique_visitors || 0));
  const w = 100;
  const h = 28;
  const stepX = weeks.length > 1 ? w / (weeks.length - 1) : w;
  const points = weeks.map((d, i) => [
    i * stepX,
    h - ((d.unique_visitors || 0) / max) * (h - 4) - 2,
  ]);
  const linePath = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = points.length
    ? `${linePath} L${points[points.length - 1][0]},${h} L0,${h} Z`
    : "";

  // Friendly "Sun 6/1 – Sat 6/7" label for the hover title on the tile
  const fmtRange = (s, e) => {
    if (!s || !e) return "";
    try {
      const sd = new Date(s + "T00:00:00Z");
      const ed = new Date(e + "T00:00:00Z");
      const opt = { month: "numeric", day: "numeric", timeZone: "UTC" };
      return `${sd.toLocaleDateString(undefined, opt)} – ${ed.toLocaleDateString(undefined, opt)}`;
    } catch {
      return `${s} – ${e}`;
    }
  };
  const rangeLabel = fmtRange(thisWeek.week_start, thisWeek.week_end);

  return (
    <div
      className={
        "rounded-2xl border bg-card p-4 " +
        (tone === "primary" ? "border-primary/40 ring-1 ring-primary/10" : "border-border")
      }
      data-testid="admin-kpi-weekly-visitors"
      title={
        rangeLabel
          ? `Visitors counted Sun→Sat (${rangeLabel} UTC). Same person across the week counts once.`
          : "Visitors counted Sun→Sat. Same person across the week counts once."
      }
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Visitors this week
        </div>
        <UsersThree weight="duotone" className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div
          className="font-display font-bold text-2xl leading-none"
          data-testid="admin-kpi-weekly-visitors-value"
        >
          {thisWeek.unique_visitors || 0}
        </div>
        {deltaTone !== "muted" && deltaTone !== "flat" && (
          <span
            className={
              "inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 " +
              (deltaTone === "up"
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "bg-rose-500/10 text-rose-600 dark:text-rose-400")
            }
            title={`vs. prior 3-week avg (${priorAvg.toFixed(1)})`}
            data-testid="admin-kpi-weekly-visitors-delta"
          >
            {deltaTone === "up" ? (
              <ArrowUp weight="bold" className="h-2.5 w-2.5" />
            ) : (
              <ArrowDown weight="bold" className="h-2.5 w-2.5" />
            )}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
      <div
        className="mt-0.5 text-[11px] text-muted-foreground"
        data-testid="admin-kpi-weekly-visitors-total"
      >
        {(thisWeek.total_visits || 0).toLocaleString()} total visits
      </div>
      {!errored && weeks.length > 0 && (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="mt-2 w-full h-7 overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="weeklyVisitorsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(234 88 12)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="rgb(234 88 12)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaPath && <path d={areaPath} fill="url(#weeklyVisitorsFill)" />}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="rgb(234 88 12)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {points.length > 0 && (
            <circle
              cx={points[points.length - 1][0]}
              cy={points[points.length - 1][1]}
              r="2"
              fill="rgb(234 88 12)"
            />
          )}
        </svg>
      )}
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        4 weeks · Sun–Sat
      </div>
    </div>
  );
}
